import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { getCache, setCache } from '../utils/cache';
import RenderMonitor from '../components/RenderMonitor';

function ClientBootcamps() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const passedClientName = location?.state?.clientName || '';
  const [bootcamps, setBootcamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clientName, setClientName] = useState('');

  useEffect(() => {
    const run = async () => {
      setError('');
      const BOOTCAMPS_KEY = `client.${id}.allBootcamps.v1`;
      const SNAPSHOT_KEY = `client.${id}.snapshot.v2`;

      // Seed UI from passed state and caches
      const passedName = passedClientName;
      const snapshot = getCache(SNAPSHOT_KEY, 2 * 60 * 1000);
      const bootcampsCache = getCache(BOOTCAMPS_KEY, 2 * 60 * 1000);
      const nameFromSnapshot = snapshot?.client?.business_name || '';
      const cachedName = bootcampsCache?.clientName || '';
      const initialName = passedName || nameFromSnapshot || cachedName || '';
      if (initialName) setClientName(initialName);

      if (bootcampsCache && Array.isArray(bootcampsCache.bootcamps)) {
        setBootcamps(bootcampsCache.bootcamps);
        setLoading(false);
      } else {
        setLoading(true);
      }

      // Background revalidation
      let teamIds = Array.isArray(snapshot?.teams) ? snapshot.teams.map(t => t.id) : null;
      if (!teamIds || teamIds.length === 0) {
        console.log('[CLIENT_BOOTCAMPS][DB] SELECT teams');
        const { data: teamsData } = await supabase
          .from('teams')
          .select('id')
          .eq('client_id', id);
        teamIds = (teamsData || []).map(t => t.id);
      }

      let fetchedClientName = initialName;
      if (!fetchedClientName) {
        console.log('[CLIENT_BOOTCAMPS][DB] SELECT client name');
        const { data: clientData } = await supabase
          .from('clients')
          .select('business_name')
          .eq('id', id)
          .single();
        if (clientData?.business_name) {
          fetchedClientName = clientData.business_name;
          setClientName(clientData.business_name);
        }
      }

      let allBootcamps = [];
      if (teamIds && teamIds.length > 0) {
        console.log('[CLIENT_BOOTCAMPS][DB] SELECT bootcamps for teams', { teams: teamIds.length });
        const { data: bootcampsData, error: bootcampsError } = await supabase
          .from('bootcamps')
          .select('id, start_date, end_date, stutter_time, team_id, courses(name), teams(name)')
          .in('team_id', teamIds);
        if (bootcampsError) {
          setError('Could not fetch bootcamps');
          setLoading(false);
          return;
        }
        allBootcamps = bootcampsData || [];
      }
      setBootcamps(allBootcamps);

      setCache(BOOTCAMPS_KEY, { clientName: fetchedClientName || initialName || '', bootcamps: allBootcamps });
      setLoading(false);
    };
    run();
  }, [id, passedClientName]);

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(new Date(date));
  };

  // Group bootcamps by team
  const grouped = {};
  bootcamps.forEach(bc => {
    const teamName = bc.teams?.name || 'Unknown Team';
    if (!grouped[teamName]) grouped[teamName] = [];
    grouped[teamName].push(bc);
  });

  return (
    <div className="modern-container-narrow">
      <RenderMonitor componentName="ClientBootcamps" />
      <button onClick={() => navigate(-1)} className="modern-button modern-button-secondary modern-mb-6">
        ← Back
      </button>
      <div className="modern-card">
        <h1 className="modern-title">All Bootcamps for {clientName || 'Client'}</h1>
        {loading ? (
          <div className="modern-loading">Loading bootcamps...</div>
        ) : error ? (
          <div className="modern-error">{error}</div>
        ) : bootcamps.length === 0 ? (
          <p style={{ color: '#64748b', fontStyle: 'italic' }}>No bootcamps for this client.</p>
        ) : (
          <div className="modern-flex modern-flex-col modern-gap-8">
            {Object.entries(grouped).map(([team, bcs]) => (
              <div key={team}>
                <h2 className="modern-heading">{team}</h2>
                <div className="modern-flex modern-flex-col modern-gap-4">
                  {bcs.map(bc => (
                    <div key={bc.id} className="modern-card">
                      <div className="modern-flex modern-justify-between modern-items-center">
                        <div>
                          <div style={{ fontWeight: 600 }}>{bc.courses?.name || 'Unknown Course'}</div>
                          <div style={{ color: '#64748b', fontSize: 14 }}>
                            {formatDate(bc.start_date)} - {formatDate(bc.end_date)}
                          </div>
                        </div>
                        <button
                          className="modern-button modern-button-info"
                          onClick={() => navigate(`/bootcamp/${bc.id}`)}
                        >
                          View Bootcamp →
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientBootcamps; 